import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {observer, inject} from "mobx-react";
import {Container, Col, Row} from "react-bootstrap";

import copy from "./copy";
import { isE2ETest } from '../../../prequal/about-you/about-you-helpers';
import {FormHeader} from "../../../components/form-header/form-header";
import {FancySpinner} from "../../../components/spinners/fancy-spinner";
import {VehicleFinancingUnavailable} from "../../../components/vehicle-financing-unavailable";
import {Captcha} from "../../../components/captcha";
import {DisclosureBlock} from "../../../components/disclosure-block";
import {BrandingValueProp} from "../../../components/branding";
import {isInIFrame, postMessageScrollIntoView} from "../../../utils/iframe-utils";
import {DigitalCreditAppStepStore} from "./digital-credit-app-step-store";
import {ServerError} from "../../../components/interstitials";
import {
    CityField,
    DateOfBirthField,
    EmailField,
    EmployerDateField,
    EmployerNameField,
    EmployerPhoneNumberField,
    Street1Field,
    Street2Field,
    YearlyIncomeField,
    JobTitleField,
    MonthlyHousingPaymentField,
    PhoneNumberField,
    ResidentialDateField,
    SSNField,
    StateField,
    ZipcodeField,
    EmploymentStatusField,
    CitizenshipStatusField,
    ResidentialStatusField,
    TradeInIntentField,
    FirstNameField,
    LastNameField,
    SubmitFormButton,
    CoapplicantIntentField,
    RelationshipToPrimaryField,
    ReusePrimaryApplicantAddressField,
} from "../../../form-fields";
import { logVehicleFinancingUnavailable } from "../../../prequal/about-you/about-you-helpers";

export const createCustomerEventHandler = (store, removeEventListener) => {
  const customerListenerTimeout = setTimeout(() => {
    removeEventListener();
  }, 6000);

  const customerEventHandler = (event) => {
    if (typeof event.data === 'object' && event.data !== null) {
      if ('customer' in event.data) {
        store.replaceFieldValues(event.data.customer);
        clearTimeout(customerListenerTimeout);
        removeEventListener();
      }
    }
  };

  return customerEventHandler;
};

export const DigitalCreditAppStep = inject(
    "apiStore",
    "uxCopyStore",
    "partnerStore",
    "dealershipStore",
    "themeStore",
    "historyStore",
    "rideOctaneStore",
    "waffleStore",
    "flowStore",
    "userStore",
)(
    observer(
        class _DigitalCreditAppStep extends React.Component {
            static propTypes = {
                accessoriesAmount: PropTypes.number,
                hasFetchedVehicle: PropTypes.bool,
                vehicleConfiguration: PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    msrp: PropTypes.number.isRequired,
                    default_image: PropTypes.string.isRequired,
                }),
                isDummyVehicleEnabled: PropTypes.bool.isRequired,
                rawVehicleData: PropTypes.object,
                isCoapplicantFlow: PropTypes.bool,
            };

            captchaRef = React.createRef();
            store = new DigitalCreditAppStepStore(this.props);

            componentDidMount() {
               window.scrollTo(0, 0);
               if (this.captchaRef && this.captchaRef.current) {
                  this.captchaRef.current.reset();
                }

                this.customerEventHandler = createCustomerEventHandler(
                  this.store,
                  () => window.removeEventListener('message', this.customerEventHandler)
                );

                window.addEventListener('message', this.customerEventHandler)


            }

            componentWillUnmount() {
                window.removeEventListener('message', this.customerEventHandler);
            }

            launchCaptcha = () => {
                const email = this.store.fieldStates.email.value;
                if (isInIFrame()) {
                    postMessageScrollIntoView(window.top);
                } else {
                    window.scrollTo(0, 0);
                }
                /*
                 * Skip reCAPTCHA for e2e tests and co-applicant step.
                 * If the user is at co-applicant step it means they already started the flow
                 * and solved the captcha challenge when starting it.
                 */
                if (isE2ETest(this.store.rideOctaneStore.settings.ENVIRONMENT_KEY, email) || this.props.flowStore.isCoapplicantFlow) {
                    this.store.submitForm(
                        null,
                        this.props.vehicleConfiguration,
                        this.props.rawVehicleData,
                        this.props.flowStore.updateApplicationUuid,
                        this.props.flowStore.updateApplicantUuids,
                    )
                } else {
                    this.captchaRef.current.execute();
                }
            };

            render() {
                if(this.store.showServerError) {
                    return (
                        <Container data-oid="digital-credit-app-step-error-page">
                            <ServerError origin="digital-credit-app-step" errorMessage={this.store.serverErrorMessage}/>
                        </Container>
                    );
                }

                // If haven't fetched the vehicle and we need the vehicle for the VehicleHeader display the fancy loading spinner
                if (this.store.headerType === "VehicleHeader" && !this.props.hasFetchedVehicle){
                    return (
                        <Container data-oid="digital-credit-app-loading">
                            <FancySpinner className="d-block mx-auto my-5" />
                        </Container>
                    );
                }

                // If the vehicle has been fetched and there is no vehicle configuration and this partner doesn't decision on dummmy vehicles
                if (this.props.hasFetchedVehicle && !this.props.vehicleConfiguration && !this.props.isDummyVehicleEnabled) {
                    logVehicleFinancingUnavailable(
                        this.props.partnerStore.partnerIdentifier,
                        this.props.hasFetchedVehicle,
                        this.props.vehicleConfiguration,
                        this.props.isDummyVehicleEnabled
                        );
                    return (
                        <Container data-oid="digital-credit-app-unavailable">
                            <VehicleFinancingUnavailable />
                        </Container>
                    );
                }

                return (
                    <Container data-oid="digital-credit-app-container" key="primary_applicant_flow">
                        <div className={classNames("w-450px mw-100 mx-auto")}>
                                <FormHeader
                                    headerType={this.store.headerType}
                                    vehicleConfiguration={this.props.vehicleConfiguration}
                                    accessoriesAmount={this.props.accessoriesAmount}
                                    title={this.store.formHeaderTitle}
                                    isCoapplicantFlow={this.props.isCoapplicantFlow}
                                />

                            <form id="digital-credit-app-form">
                                {this.props.isCoapplicantFlow &&
                                    <RelationshipToPrimaryField
                                        controlId={"relationship_to_primary"}
                                        value={this.store.fieldStates["relationship_to_primary"].value}
                                        error={this.store.getVisibleFieldError("relationship_to_primary")}
                                        onChange={(event) => this.store.updateFieldValue(event, "relationship_to_primary")}
                                    />
                                }

                                <Row>
                                    <Col xs={6}>
                                        <FirstNameField
                                            controlId={"first_name"}
                                            value={this.store.fieldStates["first_name"].value}
                                            error={this.store.getVisibleFieldError("first_name")}
                                            onChange={(event) => this.store.updateFieldValue(event, "first_name")}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <LastNameField
                                            controlId={"last_name"}
                                            value={this.store.fieldStates["last_name"].value}
                                            error={this.store.getVisibleFieldError("last_name")}
                                            onChange={(event) => this.store.updateFieldValue(event, "last_name")}
                                        />
                                    </Col>
                                </Row>

                                <EmailField
                                    controlId={"email"}
                                    value={this.store.fieldStates["email"].value}
                                    error={this.store.getVisibleFieldError("email")}
                                    onChange={(event) => this.store.updateFieldValue(event, "email")}
                                />

                                <DateOfBirthField
                                    controlId={"date_of_birth"}
                                    value={this.store.fieldStates["date_of_birth"].value}
                                    error={this.store.getVisibleFieldError("date_of_birth")}
                                    onChange={(event) => this.store.updateFieldValue(event, "date_of_birth")}
                                />

                                {this.props.isCoapplicantFlow && this.store.primaryApplicantAddressData &&
                                    <ReusePrimaryApplicantAddressField
                                        controlId={"reuse_primary_address"}
                                        value={this.store.fieldStates["reuse_primary_address"].value}
                                        error={this.store.getVisibleFieldError("reuse_primary_address")}
                                        onChange={(event) => this.store.onReusePrimaryAddressChange(event)}
                                    />
                                }

                                <Street1Field
                                    controlId={"street1"}
                                    value={this.store.fieldStates["street1"].value}
                                    error={this.store.getVisibleFieldError("street1")}
                                    onChange={(event) => this.store.updateFieldValue(event, "street1")}
                                    required={!this.store.isReusingPrimaryAddress}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <Street2Field
                                    controlId={"street2"}
                                    value={this.store.fieldStates["street2"].value}
                                    error={this.store.getVisibleFieldError("street2")}
                                    onChange={(event) => this.store.updateFieldValue(event, "street2")}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <CityField
                                    controlId={"city"}
                                    value={this.store.fieldStates["city"].value}
                                    error={this.store.getVisibleFieldError("city")}
                                    onChange={(event) => this.store.updateFieldValue(event, "city")}
                                    required={!this.store.isReusingPrimaryAddress}
                                    disabled={this.store.isReusingPrimaryAddress}
                                />

                                <Row>
                                    <Col xs={6}>
                                        <StateField
                                            controlId={"state"}
                                            error={this.store.getVisibleFieldError("state")}
                                            value={this.store.fieldStates["state"].value}
                                            onChange={(event) => this.store.updateFieldValue(event, "state")}
                                            required={!this.store.isReusingPrimaryAddress}
                                            disabled={this.store.isReusingPrimaryAddress}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <ZipcodeField
                                            controlId={"zip_code"}
                                            value={this.store.fieldStates["zip_code"].value}
                                            error={this.store.getVisibleFieldError("zip_code")}
                                            onChange={(event) => this.store.updateFieldValue(event, "zip_code")}
                                            required={!this.store.isReusingPrimaryAddress}
                                            disabled={this.store.isReusingPrimaryAddress}
                                        />
                                    </Col>
                                </Row>

                                <ResidentialStatusField
                                    controlId={"residential_status"}
                                    value={this.store.fieldStates["residential_status"].value}
                                    error={this.store.getVisibleFieldError("residential_status")}
                                    onChange={(event) => this.store.updateFieldValue(event, "residential_status")}
                                />

                                <ResidentialDateField
                                    controlId={"residential_date"}
                                    value={this.store.fieldStates["residential_date"].value}
                                    error={this.store.getVisibleFieldError("residential_date")}
                                    onChange={(event) => this.store.updateFieldValue(event, "residential_date")}
                                />

                                <EmploymentStatusField
                                    controlId={"employment_status"}
                                    value={this.store.fieldStates["employment_status"].value}
                                    error={this.store.getVisibleFieldError("employment_status")}
                                    onChange={(event) => this.store.onEmploymentStatusChange(event)}
                                />

                                <YearlyIncomeField
                                    controlId={"yearly_income"}
                                    value={this.store.fieldStates["yearly_income"].value}
                                    error={this.store.getVisibleFieldError("yearly_income")}
                                    onChange={(event) => this.store.updateFieldValue(event, "yearly_income")}
                                    toolTipText={"Note"}
                                    toolTipMsg={"Alimony, child support, or separate maintenance income need not be revealed if you do not wish to have it considered as a basis for repaying this obligation or for account maintenance."}
                                    required={this.store.employmentFieldConditionality["yearly_income"].required}
                                />

                                <PhoneNumberField
                                    controlId={"phone_number"}
                                    value={this.store.fieldStates["phone_number"].value}
                                    error={this.store.getVisibleFieldError("phone_number")}
                                    onChange={(event) => this.store.updateFieldValue(event, "phone_number")}
                                />

                                <CitizenshipStatusField
                                    controlId={"citizenship_status"}
                                    value={this.store.fieldStates["citizenship_status"].value}
                                    error={this.store.getVisibleFieldError("citizenship_status")}
                                    onChange={(event) => this.store.updateFieldValue(event, "citizenship_status")}
                                />

                                <MonthlyHousingPaymentField
                                    controlId={"monthly_housing_payment"}
                                    value={this.store.fieldStates["monthly_housing_payment"].value}
                                    error={this.store.getVisibleFieldError("monthly_housing_payment")}
                                    onChange={(event) => this.store.updateFieldValue(event, "monthly_housing_payment")}
                                />

                                <EmployerNameField
                                    controlId={"employer_name"}
                                    value={this.store.fieldStates["employer_name"].value}
                                    error={this.store.getVisibleFieldError("employer_name")}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_name")}
                                    required={this.store.employmentFieldConditionality["employer_name"].required}
                                    disabled={this.store.employmentFieldConditionality["employer_name"].disabled}
                                />

                                <EmployerPhoneNumberField
                                    controlId={"employer_phone"}
                                    value={this.store.fieldStates["employer_phone"].value}
                                    error={this.store.getVisibleFieldError("employer_phone")}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_phone")}
                                    required={this.store.employmentFieldConditionality["employer_phone"].required}
                                />

                                <JobTitleField
                                    controlId={"job_title"}
                                    value={this.store.fieldStates["job_title"].value}
                                    error={this.store.getVisibleFieldError("job_title")}
                                    onChange={(event) => this.store.updateFieldValue(event, "job_title")}
                                    required={this.store.employmentFieldConditionality["job_title"].required}
                                    disabled={this.store.employmentFieldConditionality["job_title"].disabled}
                                />

                                <EmployerDateField
                                    controlId={"employer_date"}
                                    value={this.store.fieldStates["employer_date"].value}
                                    error={this.store.getVisibleFieldError("employer_date")}
                                    onChange={(event) => this.store.updateFieldValue(event, "employer_date")}
                                    required={this.store.employmentFieldConditionality["employer_date"].required}
                                    disabled={this.store.employmentFieldConditionality["employer_date"].disabled}
                                />

                                <SSNField
                                    controlId={"social_security_number"}
                                    value={this.store.fieldStates["social_security_number"].value}
                                    error={this.store.getVisibleFieldError("social_security_number")}
                                    onChange={(event) => this.store.updateFieldValue(event, "social_security_number")}
                                />

                                <TradeInIntentField
                                    controlId={"trade_in_intent"}
                                    value={this.store.fieldStates["trade_in_intent"].value}
                                    error={this.store.getVisibleFieldError("trade_in_intent")}
                                    onChange={(event) => this.store.updateFieldValue(event, "trade_in_intent")}
                                    partnerStore={this.store.partnerStore}
                                />

                                {this.store.showCoapplicantIntentField &&
                                    <CoapplicantIntentField
                                        controlId={"coapplicant_intent"}
                                        value={this.store.fieldStates["coapplicant_intent"].value}
                                        error={this.store.getVisibleFieldError("coapplicant_intent")}
                                        onChange={(event) => this.store.updateFieldValue(event, "coapplicant_intent")}
                                    />
                                }

                                <div>
                                    <BrandingValueProp
                                        showPoweredByOctane={this.store.showPoweredByOctane}
                                        showDesktopLayout={!!this.props.partnerStore.hasMultiColumnDisplay}
                                        showMiniProp
                                    />

                                    <DisclosureBlock
                                        id="consent"
                                        showCheckBox
                                        showRuleLine
                                        disclosureText={this.store.prequalDisclosure}
                                        onChange={this.store.updatePrequalDisclosureConsent}
                                        isChecked={this.store.prequalDisclosureConsent}
                                    />
                                    <DisclosureBlock
                                        id="text-consent"
                                        showCheckBox
                                        disclosureText={copy.text_consent.labelContent}
                                        onChange={this.store.updateTextConsent}
                                        isChecked={this.store.textConsent}
                                    />
                                    <Captcha
                                        captchaRef={this.captchaRef}
                                        onClickContinue={(captcha) =>
                                        this.store.submitForm(
                                            captcha,
                                            this.props.vehicleConfiguration,
                                            this.props.rawVehicleData,
                                            this.props.flowStore.updateApplicationUuid,
                                            this.props.flowStore.updateApplicantUuids,
                                        )}
                                    />

                                    <SubmitFormButton
                                        canContinue={
                                            this.props.hasFetchedVehicle
                                            && this.store.prequalDisclosureConsent
                                            && this.store.isFormValid
                                            && this.store.textConsent
                                        }
                                        oid='cta-digital-credit-app'
                                        onClick={this.launchCaptcha}
                                        continueButtonLabel={this.store.continueButtonLabel}
                                        partnerStore={this.store.partnerStore}
                                    />
                                </div>
                            </form>
                        </div>
                    </Container>
                );
            }
        },
    ),
);
